import {
  Table,
  Badge,
  Text,
  HStack,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, MoreVertical, Eye, Trash2, ArrowRightLeft } from 'lucide-react'
import type { UnitWithResidence } from '../types'

type SortField = 'unit_number' | 'unit_type' | 'floor' | 'residence' | 'general_shares'
type SortDirection = 'asc' | 'desc'

interface UnitTableProps {
  units: UnitWithResidence[]
  selectedUnitId: string | null
  onUnitClick: (id: string) => void
  onDelete?: (id: string) => void
  onManageMutations?: (id: string) => void
  canDelete?: (residenceId: string) => boolean
  sortField?: SortField
  sortDirection?: SortDirection
  onSort?: (field: SortField) => void
}

export function UnitTable({
  units,
  selectedUnitId,
  onUnitClick,
  onDelete,
  onManageMutations,
  canDelete,
  sortField,
  sortDirection,
  onSort,
}: UnitTableProps) {
  const { t } = useTranslation()

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null
    return sortDirection === 'asc' ? (
      <ArrowUp size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    ) : (
      <ArrowDown size={14} style={{ display: 'inline', marginLeft: '4px' }} />
    )
  }

  const getUnitTypeColor = (type: string | null) => {
    switch (type) {
      case 'RESIDENTIAL':
        return 'blue'
      case 'PARKING':
        return 'purple'
      case 'CELLAR':
        return 'gray'
      case 'COMMERCIAL':
        return 'green'
      default:
        return 'gray'
    }
  }

  const getFloorDisplay = (floor: number | null) => {
    if (floor === null) return '-'
    if (floor === 0) return 'RDC'
    if (floor < 0) return `S-${Math.abs(floor)}`
    return `${floor}e`
  }

  const getOwnerDisplay = (unit: UnitWithResidence) => {
    if (!unit.ownerships || unit.ownerships.length === 0) return '-'
    const owners = unit.ownerships
      .filter((o) => o.owners)
      .map((o) => `${o.owners!.first_name} ${o.owners!.last_name}`)
    if (owners.length === 0) return '-'
    if (owners.length === 1) return owners[0]
    return `${owners[0]} +${owners.length - 1}`
  }

  const getResidenceInfo = (unit: UnitWithResidence) => {
    if (!unit.residences) return null
    return {
      id: unit.residences.id,
      key: unit.residences.residence_key,
      name: unit.residences.name,
    }
  }

  return (
    <Table.Root variant="outline" size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader width="200px">Résidence</Table.ColumnHeader>
          <Table.ColumnHeader
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('unit_number')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('units.fields.unit_number')}
            <SortIcon field="unit_number" />
          </Table.ColumnHeader>
          <Table.ColumnHeader
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('unit_type')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('units.fields.unit_type')}
            <SortIcon field="unit_type" />
          </Table.ColumnHeader>
          <Table.ColumnHeader
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('floor')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('units.fields.floor')}
            <SortIcon field="floor" />
          </Table.ColumnHeader>
          <Table.ColumnHeader>{t('units.fields.stairwell')}</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right">{t('units.fields.area_sqm')}</Table.ColumnHeader>
          <Table.ColumnHeader
            textAlign="right"
            cursor={onSort ? 'pointer' : undefined}
            onClick={() => onSort?.('general_shares')}
            userSelect="none"
            _hover={onSort ? { bg: 'gray.50' } : undefined}
          >
            {t('units.fields.general_shares')}
            <SortIcon field="general_shares" />
          </Table.ColumnHeader>
          <Table.ColumnHeader>Propriétaire</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="center" width="80px">{t('common.actions')}</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {units.map((unit) => {
          const residence = getResidenceInfo(unit)
          return (
            <Table.Row
              key={unit.id}
              data-unit-row={unit.id}
              onClick={() => onUnitClick(unit.id)}
              cursor="pointer"
              bg={selectedUnitId === unit.id ? 'brand.50' : undefined}
              _hover={{
                bg: selectedUnitId === unit.id ? 'brand.100' : 'gray.50',
              }}
            >
              <Table.Cell>
                {residence ? (
                  <Badge size="sm" colorPalette="gray" variant="subtle">
                    {residence.key ? (
                      <>
                        <Text as="span" fontWeight="bold">
                          {residence.key}
                        </Text>{' '}
                        {residence.name}
                      </>
                    ) : (
                      residence.name
                    )}
                  </Badge>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    -
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text fontFamily="mono" fontWeight="medium">
                  {unit.unit_number}
                </Text>
              </Table.Cell>
              <Table.Cell>
                {unit.unit_type && (
                  <Badge colorPalette={getUnitTypeColor(unit.unit_type)} size="sm">
                    {t(`units.unit_types.${unit.unit_type}`)}
                  </Badge>
                )}
              </Table.Cell>
              <Table.Cell>
                <Text fontSize="sm">{getFloorDisplay(unit.floor)}</Text>
              </Table.Cell>
              <Table.Cell>
                {unit.stairwell && (
                  <Text fontSize="sm" fontFamily="mono">
                    {unit.stairwell}
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                {unit.area_sqm && (
                  <HStack justify="flex-end" gap={1}>
                    <Text fontSize="sm">{unit.area_sqm}</Text>
                    <Text fontSize="xs" color="gray.500">
                      m²
                    </Text>
                  </HStack>
                )}
              </Table.Cell>
              <Table.Cell textAlign="right">
                <Text fontSize="sm" fontWeight="medium">
                  {unit.general_shares || 0}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text fontSize="sm">{getOwnerDisplay(unit)}</Text>
              </Table.Cell>
              <Table.Cell onClick={(e) => e.stopPropagation()}>
                <HStack justify="center">
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        aria-label={t('common.actions')}
                      >
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem
                            value="details"
                            onClick={(e) => {
                              e.stopPropagation()
                              onUnitClick(unit.id)
                            }}
                          >
                            <HStack gap={2}>
                              <Eye size={16} />
                              <Text>{t('common.details')}</Text>
                            </HStack>
                          </MenuItem>
                          {onManageMutations && (
                            <MenuItem
                              value="mutations"
                              onClick={(e) => {
                                e.stopPropagation()
                                onManageMutations(unit.id)
                              }}
                            >
                              <HStack gap={2}>
                                <ArrowRightLeft size={16} />
                                <Text>Gérer les mutations</Text>
                              </HStack>
                            </MenuItem>
                          )}
                          {onDelete && canDelete?.(unit.residence_id) && (
                            <MenuItem
                              value="delete"
                              onClick={(e) => {
                                e.stopPropagation()
                                onDelete(unit.id)
                              }}
                              color="red.600"
                              _hover={{ bg: 'red.50' }}
                            >
                              <HStack gap={2}>
                                <Trash2 size={16} />
                                <Text>{t('common.delete')}</Text>
                              </HStack>
                            </MenuItem>
                          )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </HStack>
              </Table.Cell>
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table.Root>
  )
}
